import Box from "@material-ui/core/esm/Box";
import NavigateNext from "@material-ui/icons/esm/NavigateNext";
import InfoOutlined from "@material-ui/icons/esm/InfoOutlined";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';
import { EnumInvoiceStatus } from 'api';
import getCreditCardFee from 'api/invoices/getCreditCardFee';
import { DownloadButton, PrimaryButton, SecondaryButton } from 'components/Button';
import Dialog from 'components/Dialog/Dialog';
import Divider from 'components/Divider/Divider';
import InventoryIcon from 'components/Icons/InventoryIcon';
import Spinner from 'components/Spinner/Spinner';
import { Info, BoldSubtitle } from 'components/Text';
import { LIGHT_PRIMARY } from 'constants/colors';
import { NOT_AVAILABLE } from 'constants/text';
import useDownloadInvoice from 'hooks/invoices/useDownloadInvoice';
import useRequestPaymentLink from 'hooks/invoices/useRequestPaymentLink';
import { INVOICE_DETAILS } from 'routes/invoices/paths';
import dateTimeFormatter from 'utils/date/dateTimeFormatter';
import getInvoiceTranslationString from 'utils/invoices/getInvoiceTranslationString';
import isInvoiceArchived from 'utils/invoices/isInvoiceArchived';
import checkLoadable from 'utils/recoil/checkLoadable';
import useStyles from '../InvoiceStyles';
export default function InvoiceInfo(props) {
  const {
    invoiceNumber = '',
    totalPriceVatIncluded,
    dueDate,
    invoiceStatus,
    agreementName,
    actualAmountPaid,
    currency,
    archiveDetails
  } = props;
  const {
    push
  } = useHistory();
  const {
    t
  } = useTranslation();
  const {
    button,
    buttonText,
    row,
    col,
    colRight,
    statusStyle
  } = useStyles();
  const invoiceStatusString = getInvoiceTranslationString(invoiceStatus);
  const {
    downloadInvoice,
    downloadIsLoading
  } = useDownloadInvoice(props);
  const {
    requestPaymentLink,
    paymentLinkIsLoading
  } = useRequestPaymentLink(props);
  const [openPayNowWarning, setOpenPayNowWarning] = useState(false);
  const creditCardFeeLoadable = useRecoilValueLoadable(getCreditCardFee);
  const {
    contents: creditCardFee
  } = checkLoadable(creditCardFeeLoadable);
  const isInvoiceNotDownloadable = invoiceStatus === EnumInvoiceStatus.Pending || invoiceStatus === EnumInvoiceStatus.Cancelled || invoiceStatus === EnumInvoiceStatus.Preparing || invoiceStatus === EnumInvoiceStatus.Initial || invoiceStatus === EnumInvoiceStatus.SentToDistribution || invoiceStatus === EnumInvoiceStatus.AwaitingDelivery || invoiceStatus === EnumInvoiceStatus.PreparingDistribution || invoiceStatus === EnumInvoiceStatus.DocumentError || invoiceStatus === EnumInvoiceStatus.ValidationError;
  const isInvoicePayableNow = (invoiceStatus === EnumInvoiceStatus.PaymentRequested || invoiceStatus === EnumInvoiceStatus.PaymentReminderSent || invoiceStatus === EnumInvoiceStatus.ExternalDebtCollection) && totalPriceVatIncluded && parseFloat(totalPriceVatIncluded) <= 5500;
  const displayDueDate = !(invoiceStatus === EnumInvoiceStatus.Remittance || invoiceStatus === EnumInvoiceStatus.PaidByBalance);
  const amountPaid = actualAmountPaid?.toFixed(2);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    className: row
  }, /*#__PURE__*/React.createElement(Box, {
    className: col
  }, /*#__PURE__*/React.createElement(BoldSubtitle, null, `${t('invoices.invoice_number')} ${invoiceNumber}`), displayDueDate && /*#__PURE__*/React.createElement(Info, null, `${t('invoices.due_date')}: ${dateTimeFormatter(dueDate)}`), /*#__PURE__*/React.createElement(Info, null, `${t('agreements.agreement')}: ${agreementName ?? NOT_AVAILABLE}`), actualAmountPaid !== 0 && /*#__PURE__*/React.createElement(Info, null, t('invoices.amount_paid'), ":", currency ? `${amountPaid} ${currency}` : amountPaid)), /*#__PURE__*/React.createElement(Box, {
    className: colRight
  }, /*#__PURE__*/React.createElement(BoldSubtitle, null, `${t('invoices.kr')} ${totalPriceVatIncluded}`), /*#__PURE__*/React.createElement("div", {
    className: statusStyle
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "right",
    gridColumnGap: 3
  }, isInvoiceArchived({
    archiveDetails
  }) && /*#__PURE__*/React.createElement(InventoryIcon, {
    title: t('invoices.archived_info')
  }), t(`invoice_status.${invoiceStatusString}`))))), /*#__PURE__*/React.createElement(Dialog, {
    open: openPayNowWarning,
    onClose: () => setOpenPayNowWarning(false),
    dialogTitle: t('invoices.credit_card_fee_warning_title'),
    onPrimaryBtnClick: () => {
      requestPaymentLink();
      setOpenPayNowWarning(false);
    },
    primaryBtnTitle: t('buttons.pay_now'),
    isLoading: paymentLinkIsLoading,
    primaryBtnClickIsDisabled: false
  }, /*#__PURE__*/React.createElement(Box, {
    fontSize: "medium"
  }, t('invoices.credit_card_fee_warning', {
    creditCardFee
  })), /*#__PURE__*/React.createElement(Box, {
    bgcolor: LIGHT_PRIMARY,
    padding: "2rem",
    borderRadius: "5px",
    fontSize: "medium",
    position: "relative",
    textAlign: "center",
    mt: 2
  }, /*#__PURE__*/React.createElement(InfoOutlined, {
    style: {
      position: 'absolute',
      top: '1rem',
      left: '1rem'
    }
  }), t('invoices.payment_invoice_customer_information_box'))), isInvoicePayableNow && /*#__PURE__*/React.createElement(Box, {
    className: colRight
  }, /*#__PURE__*/React.createElement(PrimaryButton, {
    onClick: () => setOpenPayNowWarning(true),
    endIcon: paymentLinkIsLoading ? /*#__PURE__*/React.createElement(Spinner, {
      isButtonProgress: true
    }) : /*#__PURE__*/React.createElement(NavigateNext, null),
    className: button,
    disabled: paymentLinkIsLoading
  }, /*#__PURE__*/React.createElement("span", {
    className: buttonText
  }, t('buttons.pay_now')))), /*#__PURE__*/React.createElement(Box, {
    className: row,
    mt: 1
  }, /*#__PURE__*/React.createElement(DownloadButton, {
    onClick: () => downloadInvoice(),
    loading: downloadIsLoading,
    disabled: isInvoiceNotDownloadable
  }), /*#__PURE__*/React.createElement(SecondaryButton, {
    onClick: () => push(INVOICE_DETAILS(invoiceNumber)),
    endIcon: /*#__PURE__*/React.createElement(NavigateNext, null),
    className: button
  }, /*#__PURE__*/React.createElement("span", {
    className: buttonText
  }, t('buttons.see_details')))), /*#__PURE__*/React.createElement(Divider, null));
}